import React from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../shared/AnalyticsWidgets";
import { formatNumber, hasNumber, validRows } from "../../utils/analyticsFormat";
import styles from "../../OrderAnalyticsPage.module.scss";

// Match the route palette used by the existing TopDirections chart.
const colors = [
  "#1E40A1", "#4064CA", "#4E7CFF", "#22C0FF", "#80DBFF",
  "#FFB5CE", "#FF98BB", "#D7ACFF", "#A471D3", "#8555B1",
];

export const routeName = (row, separator = " → ") =>
  [row?.from, row?.to].filter((value) => typeof value === "string" && value.trim()).join(separator) || "—";

export default function RoutePieChart({ routes, mode, onAskAssistant, assistantPending, showTitle = true }) {
  const { t, i18n } = useTranslation();
  const title = t(`analyticsNew.mode.${mode}`, mode === "auto" ? "Авто" : "Авиа");
  const countKey = mode === "auto" ? "autoCount" : "airCount";
  const rows = validRows(routes)
    .filter((row) => hasNumber(row[countKey]) && row[countKey] > 0)
    .sort((a, b) => b[countKey] - a[countKey]);
  const total = rows.reduce((sum, row) => sum + row[countKey], 0);
  let startAngle = -Math.PI / 2;
  const segments = rows.map((row, index) => {
    const fraction = row[countKey] / total;
    const angle = fraction * Math.PI * 2;
    const endAngle = startAngle + angle;
    const middle = startAngle + angle / 2;
    const segment = {
      row,
      color: colors[index % colors.length],
      percent: (fraction * 100).toFixed(1),
      fraction,
      path: `M 150 150 L ${150 + 140 * Math.cos(startAngle)} ${150 + 140 * Math.sin(startAngle)} A 140 140 0 ${fraction > 0.5 ? 1 : 0} 1 ${150 + 140 * Math.cos(endAngle)} ${150 + 140 * Math.sin(endAngle)} Z`,
      labelX: 150 + 84 * Math.cos(middle),
      labelY: 150 + 84 * Math.sin(middle),
    };
    startAngle = endAngle;
    return segment;
  });
  const ask = (row) => {
    if (assistantPending) return;
    onAskAssistant(
      `${t("analyticsNew.assistant.routePrefix", "Расскажи про направление ")}${routeName(row)}`,
      { tool: "route-analytics", route: routeName(row) },
    );
  };
  const description = ({ row, percent }) =>
    `${routeName(row, " — ")} ${percent}% (${formatNumber(row[countKey], i18n.language)})`;

  return (
    <section className={styles.routePieSection} aria-label={title}>
      {showTitle && <h3>{title}</h3>}
      {segments.length ? (
        <div className={styles.routePieLayout}>
          <svg className={styles.routePie} viewBox="0 0 300 300" role="group" aria-label={title}>
            <title>{title}</title>
            {segments.map((segment, index) => {
              const interactive = {
                fill: segment.color,
                role: "button",
                tabIndex: assistantPending ? -1 : 0,
                "aria-label": description(segment),
                "aria-disabled": Boolean(assistantPending),
                className: styles.routeSlice,
                onClick: () => ask(segment.row),
                onKeyDown: (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    ask(segment.row);
                  }
                },
              };
              return (
                <g key={index}>
                  {segment.fraction === 1 ? (
                    <circle cx="150" cy="150" r="140" {...interactive}>
                      <title>{description(segment)}</title>
                    </circle>
                  ) : (
                    <path d={segment.path} {...interactive}>
                      <title>{description(segment)}</title>
                    </path>
                  )}
                  {segment.fraction >= 0.03 && (
                    <text
                      x={segment.labelX}
                      y={segment.labelY}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className={styles.routeSliceLabel}
                      aria-hidden="true"
                    >
                      {segment.percent}%
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          <ul className={styles.routePieLegend}>
            {segments.map((segment, index) => (
              <li key={index}>
                <button
                  type="button"
                  className={styles.routeButton}
                  disabled={assistantPending}
                  onClick={() => ask(segment.row)}
                >
                  <span className={styles.routeSwatch} style={{ backgroundColor: segment.color }} />
                  <span className={styles.routeLabel}>{routeName(segment.row, " — ")}</span>
                  <span className={styles.routeCount}>
                    {segment.percent}% ({formatNumber(segment.row[countKey], i18n.language)})
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <EmptyState />
      )}
    </section>
  );
}
